import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';

// Base styled component for line blocks
const LineBlockContainer = styled.div`
  position: relative;
  padding: 4px 8px;
  min-height: 22px;
  transition: background-color 0.15s ease;
  border-radius: 4px;
  margin: 1px 0;
  background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.1))' : 'transparent'};
  
  &:hover {
    background-color: ${props => props.isActive ? 'var(--color-selection-bg, rgba(14, 101, 235, 0.1))' : 'var(--color-hover-bg, rgba(0, 0, 0, 0.04))'};
  }

  ${props => props.customStyles}
`;

// Editable line variant
const EditableLineContent = styled.div`
  outline: none;
  word-break: break-word;
  white-space: pre-wrap;
  width: 100%;
  min-height: 18px;
  font-family: 'Menlo', 'Monaco', 'Courier New', monospace;
  
  &:focus {
    outline: none;
  }
`;

// View-only line variant
const ViewLineContent = styled.div`
  cursor: text;
  word-break: break-word;
  white-space: pre-wrap;
  width: 100%;
  min-height: 18px;
`;

/**
 * LineBlock component for rendering editable and view-only lines
 * 
 * @param {Object} props Component props
 * @param {boolean} props.isActive Whether the line is active/being edited
 * @param {boolean} props.isEditable Whether the line should be editable
 * @param {Function} props.onClick Handler for click events
 * @param {Function} props.onBlur Handler for blur events (editable mode)
 * @param {Function} props.onKeyDown Handler for keydown events (editable mode)
 * @param {Function} props.onCompositionStart Handler for composition start (IME input)
 * @param {Function} props.onCompositionEnd Handler for composition end (IME input)
 * @param {string|Object} props.content Content to display (string for editable, React element for view)
 * @param {Object} props.customStyles Custom styles for the container
 * @param {Function} props.onContentChange Callback when content changes
 * @param {Function} props.commitContent Callback to commit content changes 
 */
const LineBlock = React.forwardRef(({ 
  isActive = false,
  isEditable = false,
  onClick,
  onBlur,
  onKeyDown,
  onCompositionStart,
  onCompositionEnd,
  content,
  customStyles = {},
  children,
  onContentChange,
  commitContent,
  ...otherProps
}, ref) => {
  const contentRef = useRef(null);
  const [isComposing, setIsComposing] = useState(false);
  const [initialContent, setInitialContent] = useState(content);
  const prevIsActiveRef = useRef(isActive);
  const savedCursorPositionRef = useRef(null);

  // Save cursor position
  const saveCursorPosition = useCallback(() => {
    if (!contentRef.current) return null;
  
    const selection = window.getSelection();
    if (!selection.rangeCount) return null;
    
    const range = selection.getRangeAt(0);
    
    // Check if the cursor is within the element
    if (!contentRef.current.contains(range.startContainer)) return null;
    
    // Save the relevant information
    return {
      startContainer: range.startContainer,
      startOffset: range.startOffset,
      endContainer: range.endContainer,
      endOffset: range.endOffset
    };
  }, []);

  // Restore cursor position
  const restoreCursorPosition = useCallback((savedPosition) => {
    if (!contentRef.current || !savedPosition) return false;
    
    try {
      const selection = window.getSelection();
      const range = document.createRange();
      
      // Try to find the nodes within the element
      if (!contentRef.current.contains(savedPosition.startContainer)) {
        // If the exact node is gone, place cursor at the end
        if (contentRef.current.lastChild) {
          if (contentRef.current.lastChild.nodeType === 3) { // Text node
            range.setStart(contentRef.current.lastChild, contentRef.current.lastChild.textContent.length);
          } else {
            range.setStartAfter(contentRef.current.lastChild);
          }
        } else {
          range.setStart(contentRef.current, 0);
        }
        range.collapse(true);
      } else {
        // Original node still exists
        range.setStart(savedPosition.startContainer, savedPosition.startOffset);
        
        if (savedPosition.endContainer && contentRef.current.contains(savedPosition.endContainer)) {
          range.setEnd(savedPosition.endContainer, savedPosition.endOffset);
        } else {
          range.collapse(true);
        }
      }
      
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    } catch (err) {
      console.error('Error restoring cursor position:', err);
      return false;
    }
  }, []);

  // Update content ref when component changes state
  useEffect(() => {
    // When line becomes active, setup for editing
    if (isActive && !prevIsActiveRef.current) {
      if (contentRef.current) {
        // Focus the element when it becomes active
        contentRef.current.focus();
        
        // Set cursor at end of content
        const selection = window.getSelection();
        const range = document.createRange();
        
        if (contentRef.current.lastChild) {
          if (contentRef.current.lastChild.nodeType === 3) { // Text node
            range.setStart(contentRef.current.lastChild, contentRef.current.lastChild.textContent.length);
          } else {
            range.setStartAfter(contentRef.current.lastChild);
          }
        } else {
          range.setStart(contentRef.current, 0);
        }
        
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // Save initial content for potential rollback
      setInitialContent(content);
    }
    
    // If line becomes inactive, commit any changes
    if (!isActive && prevIsActiveRef.current) {
      if (contentRef.current && commitContent) {
        const currentContent = contentRef.current.innerText;
        if (currentContent !== initialContent) {
          commitContent(currentContent);
        }
      }
    }
    
    prevIsActiveRef.current = isActive;
  }, [isActive, content, initialContent, commitContent]);

  // Effect to handle content updates from parent
  useEffect(() => {
    if (!isActive && contentRef.current && content !== initialContent) {
      setInitialContent(content);
    }
  }, [content, isActive, initialContent]);

  // Handle composition events for IME input
  const handleCompositionStart = useCallback(() => {
    setIsComposing(true);
    if (onCompositionStart) onCompositionStart();
  }, [onCompositionStart]);

  const handleCompositionEnd = useCallback(() => {
    setIsComposing(false);
    if (onCompositionEnd) onCompositionEnd();
    
    // Notify parent of content change
    if (contentRef.current && onContentChange && !isComposing) {
      onContentChange(contentRef.current.innerText);
    }
  }, [onCompositionEnd, onContentChange, isComposing]);

  // Handle content change
  const handleInput = useCallback(() => {
    if (contentRef.current && onContentChange && !isComposing) {
      onContentChange(contentRef.current.innerText);
    }
  }, [onContentChange, isComposing]);

  // Custom blur handler that adds content commitment
  const handleBlur = useCallback((e) => {
    if (contentRef.current && commitContent) {
      savedCursorPositionRef.current = saveCursorPosition();
      
      // Get the current content
      const currentContent = contentRef.current.innerText;
      
      // If content changed, commit it
      if (currentContent !== initialContent) {
        commitContent(currentContent);
        setInitialContent(currentContent);
      }
    }
    
    // Call parent blur handler
    if (onBlur) onBlur(e);
  }, [onBlur, commitContent, initialContent, saveCursorPosition]);

  // Custom key down handler
  const handleKeyDown = useCallback((e) => {
    // Handle ESC - revert changes
    if (e.key === 'Escape' && contentRef.current && initialContent !== contentRef.current.innerText) {
      e.preventDefault();
      // Revert the content to initial state
      if (contentRef.current) {
        contentRef.current.innerText = initialContent;
        if (onContentChange) {
          onContentChange(initialContent);
        }
      }
      return;
    }
    
    // Call parent key down handler
    if (onKeyDown) {
      onKeyDown(e);
    }
  }, [onKeyDown, initialContent, onContentChange]);
  
  // If children are provided, render them inside the container
  if (children) {
    return (
      <LineBlockContainer 
        isActive={isActive} 
        customStyles={customStyles}
        onClick={onClick}
        ref={ref}
        {...otherProps}
      >
        {children}
      </LineBlockContainer>
    );
  }
  
  // Otherwise, render either editable or view content based on isEditable prop
  return (
    <LineBlockContainer 
      isActive={isActive} 
      customStyles={customStyles}
      onClick={(e) => {
        //console.log('LineBlockContainer direct onClick fired');
        if (onClick) onClick(e);
      }}
      ref={ref}
      {...otherProps}
    >
      {isEditable ? (
        <EditableLineContent
          ref={el => {
            contentRef.current = el;
            // Handle the forwarded ref if it's a function
            if (typeof ref === 'function') {
              ref(el);
            } else if (ref) {
              ref.current = el;
            }
          }}
          contentEditable
          suppressContentEditableWarning
          dangerouslySetInnerHTML={{ __html: content }}
          onBlur={handleBlur}
          onKeyDown={handleKeyDown}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          onInput={handleInput}
          onClick={(e) => {
            // Prevent duplicate click handling - let container handle it
            e.stopPropagation();
          }}
        />
      ) : (
        <ViewLineContent 
          onClick={(e) => {
            //console.log('ViewLineContent onClick fired');
            // Let the container handle the click
            // Do not call onClick directly here to avoid duplicate handling
          }}
        >
          {content}
        </ViewLineContent>
      )}
    </LineBlockContainer>
  );
});

LineBlock.displayName = 'LineBlock';

export default LineBlock;
